import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const FILTERS = ["All", "Stud Bolts", "Nuts", "Bolts & Screws", "Stainless", "Custom"];

const GRADES = [
  {
    product: "Stud Bolt / Threaded Rod",
    standard: "ASTM A193",
    grade: "B7 · B16 · B7M",
    material: "Alloy Steel (Cr-Mo)",
    size: "M12 – M100",
    temp: "Up to 500 °C",
    tags: ["Stud Bolts"],
  },
  {
    product: "Stud Bolt (Stainless)",
    standard: "ASTM A193",
    grade: "B8 · B8M Cl.1/2",
    material: "SS 304 / SS 316",
    size: "M12 – M64",
    temp: "Corrosion service",
    tags: ["Stud Bolts", "Stainless"],
  },
  {
    product: "Heavy Hex Nut",
    standard: "ASTM A194",
    grade: "2H · 4 · 7 · 2HM",
    material: "Carbon / Alloy Steel",
    size: "M12 – M100",
    temp: "Up to 500 °C",
    tags: ["Nuts"],
  },
  {
    product: "Heavy Hex Nut (Stainless)",
    standard: "ASTM A194",
    grade: "8 · 8M",
    material: "SS 304 / SS 316",
    size: "M12 – M64",
    temp: "Corrosion service",
    tags: ["Nuts", "Stainless"],
  },
  {
    product: "Hex Head Bolt",
    standard: "IS 1367 / ISO 898-1",
    grade: "Class 8.8 · 10.9 · 12.9",
    material: "High Tensile Steel",
    size: "M6 – M64",
    temp: "Structural / general",
    tags: ["Bolts & Screws"],
  },
  {
    product: "Hex Bolt (Imperial)",
    standard: "ASTM A325 / A490",
    grade: "Type 1",
    material: "Medium Carbon / Alloy",
    size: "½\" – 1½\"",
    temp: "Structural",
    tags: ["Bolts & Screws"],
  },
  {
    product: "Countersunk Bolt",
    standard: "DIN 7991 / IS 1364",
    grade: "8.8 · A2 · A4",
    material: "CS / MS / SS",
    size: "M6 – M36",
    temp: "General engineering",
    tags: ["Bolts & Screws"],
  },
  {
    product: "Washers (Plain / Spring / Taper)",
    standard: "IS 2016 / DIN 125 / F436",
    grade: "As specified",
    material: "CS / MS / SS",
    size: "M6 – M72",
    temp: "General",
    tags: ["Nuts", "Bolts & Screws"],
  },
  {
    product: "Customised Engineering Solutions",
    standard: "Client Drawing / Sample",
    grade: "As specified",
    material: "CS / MS / Alloy / SS / Special",
    size: "Prototype to bulk",
    temp: "Engineered to duty",
    tags: ["Custom"],
  },
  {
    product: "Duplex & Special Alloy Fasteners",
    standard: "ASTM A453 / B7M / Duplex",
    grade: "Gr.660 · 2205 · Inconel",
    material: "Special Alloy",
    size: "On enquiry",
    temp: "Severe service",
    tags: ["Stainless", "Stud Bolts", "Custom"],
  },
];

export default function Grades() {
  const [filter, setFilter] = useState("All");
  const rows = filter === "All" ? GRADES : GRADES.filter((g) => g.tags.includes(filter));

  return (
    <section id="grades" className="bg-mist py-20 lg:py-28" data-testid="grades-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-9"
        >
          <div>
            <div className="flex items-center gap-3 text-[11px] sm:text-xs tracking-[0.12em] uppercase text-steel font-semibold mb-4">
              <span className="w-10 h-[2px] bg-brand" />
              Materials & Grades
            </div>
            <h2 className="font-display font-extrabold uppercase tracking-tight leading-[1.02] text-4xl sm:text-5xl text-ink">
              Specification
              <br />
              reference table
            </h2>
          </div>
          <p className="max-w-sm text-sm text-slate-600 leading-relaxed">
            Filter by product family to see the standards, grades, materials and
            size ranges we manufacture. Anything outside this table can still be
            made to your drawing.
          </p>
        </motion.div>

        <div className="flex flex-wrap gap-2 mb-7" data-testid="grades-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`grade-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={`text-[11px] tracking-[0.12em] uppercase font-bold px-4 py-2.5 rounded-full border transition-all duration-300 ${
                filter === f
                  ? "bg-navy border-navy text-white shadow-md"
                  : "border-steel-light bg-white text-navy hover:border-navy hover:shadow-sm"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto border border-steel-light rounded-xl bg-white shadow-sm">
          <table className="w-full min-w-[840px] text-left" data-testid="grades-table">
            <thead>
              <tr className="bg-navy">
                {["Product", "Standard", "Grade", "Material", "Size Range", "Service"].map((h) => (
                  <th
                    key={h}
                    className="text-[11px] tracking-[0.12em] uppercase text-white font-bold px-5 py-4 whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <AnimatePresence initial={false}>
                {rows.map((g, i) => (
                  <motion.tr
                    key={g.product}
                    layout
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.025 }}
                    className="border-t border-steel-light odd:bg-white even:bg-mist/60 hover:bg-brand/[0.06] transition-colors duration-200"
                    data-testid={`grade-row-${i + 1}`}
                  >
                    <td className="px-5 py-4 font-display font-bold uppercase text-sm text-ink whitespace-nowrap">
                      {g.product}
                    </td>
                    <td className="px-5 py-4 font-sans font-semibold text-xs text-brand font-medium whitespace-nowrap">
                      {g.standard}
                    </td>
                    <td className="px-5 py-4 font-sans font-semibold text-xs text-navy whitespace-nowrap">
                      {g.grade}
                    </td>
                    <td className="px-5 py-4 text-[13px] text-slate-700 whitespace-nowrap">{g.material}</td>
                    <td className="px-5 py-4 font-sans font-semibold text-xs text-navy whitespace-nowrap">
                      {g.size}
                    </td>
                    <td className="px-5 py-4 text-[13px] text-slate-700 whitespace-nowrap">{g.temp}</td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        <p className="mt-5 font-sans font-semibold text-[11px] tracking-[0.14em] text-slate-500" data-testid="grades-note">
          Mill test certificates supplied with every batch · Third-party inspection on request
        </p>
      </div>
    </section>
  );
}
